import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { Rnd } from 'react-rnd'
import {
  IconRightRotate,
  IconLeftRotate,
  IconCrossYtSearch,
} from '@edulastic/icons'
import { white } from '@edulastic/colors'

import { RULER, PROTRACTOR } from '../../../assessment/themes/constants'
import RulerSvg from './assets/ruler.svg'
import ProtractorSvg from './assets/protractor.svg'
import {
  RightRotateIconContainer,
  LeftRotateIconContainer,
  TransformedLeftRotateIcon,
  ProtractorLeftRotateIconContainer,
  RulerCrossButtonContainer,
  ProtractorCrossButtonContainer,
} from './styled'

// Optimized throttle function using requestAnimationFrame for smoother performance
const createThrottle = (func, delay) => {
  let lastExecTime = 0
  let rafId = null

  return function (...args) {
    const currentTime = performance.now()

    if (currentTime - lastExecTime >= delay) {
      func.apply(this, args)
      lastExecTime = currentTime
    } else if (!rafId) {
      rafId = requestAnimationFrame(() => {
        func.apply(this, args)
        lastExecTime = performance.now()
        rafId = null
      })
    }
  }
}

// Memoize initial position calculation to avoid recalculation on every render
const getInitialPosition = () => ({
  x: window.innerWidth / 2 - 200,
  y: window.innerHeight / 2 - 60,
})

const DraggableGeometryTool = ({ toolType, onToolClose }) => {
  const [rotation, setRotation] = useState(0)
  const [position, setPosition] = useState(getInitialPosition)
  const contentRef = useRef(null)
  const centerRef = useRef({ x: 0, y: 0 })
  const rotationDataRef = useRef({ angleOffset: 0, isRotating: false })
  const animationFrameRef = useRef(null)
  const eventListenersRef = useRef([])

  // Throttled rotation update for better performance
  const throttledSetRotation = useCallback(
    createThrottle((newRotation) => {
      setRotation(newRotation)
    }, 16), // ~60fps
    []
  )

  // Optimized rotation calculation
  const calculateRotation = useCallback((clientX, clientY, angleOffset) => {
    const dx = clientX - centerRef.current.x
    const dy = clientY - centerRef.current.y
    const angle = Math.atan2(dy, dx) * (180 / Math.PI)
    return angle + angleOffset
  }, [])

  const handleRotationStart = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (!contentRef.current) return

      const rect = contentRef.current.getBoundingClientRect()
      centerRef.current = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height,
      }

      // Handle both mouse and touch events
      const isTouch = e.type === 'touchstart'
      const clientX = isTouch ? e.touches[0].clientX : e.clientX
      const clientY = isTouch ? e.touches[0].clientY : e.clientY

      const initialDx = clientX - centerRef.current.x
      const initialDy = clientY - centerRef.current.y
      const initialAngle = Math.atan2(initialDy, initialDx) * (180 / Math.PI)
      rotationDataRef.current = {
        angleOffset: rotation - initialAngle,
        isRotating: true,
      }

      const handleMove = (moveEvent) => {
        if (!rotationDataRef.current.isRotating) return

        const isTouchMove = moveEvent.type === 'touchmove'
        const moveClientX = isTouchMove
          ? moveEvent.touches[0].clientX
          : moveEvent.clientX
        const moveClientY = isTouchMove
          ? moveEvent.touches[0].clientY
          : moveEvent.clientY

        // Use requestAnimationFrame for smooth updates
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }

        animationFrameRef.current = requestAnimationFrame(() => {
          const newRotation = calculateRotation(
            moveClientX,
            moveClientY,
            rotationDataRef.current.angleOffset
          )
          throttledSetRotation(newRotation)
        })
      }

      const handleEnd = () => {
        rotationDataRef.current.isRotating = false
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }

        // Clean up event listeners efficiently
        eventListenersRef.current.forEach(
          ({ element, event, handler, options }) => {
            element.removeEventListener(event, handler, options)
          }
        )
        eventListenersRef.current = []
      }

      // Store event listeners for efficient cleanup
      const eventListeners = [
        {
          element: document,
          event: 'mousemove',
          handler: handleMove,
          options: { passive: false },
        },
        {
          element: document,
          event: 'mouseup',
          handler: handleEnd,
          options: undefined,
        },
        {
          element: document,
          event: 'touchmove',
          handler: handleMove,
          options: { passive: false },
        },
        {
          element: document,
          event: 'touchend',
          handler: handleEnd,
          options: undefined,
        },
      ]

      eventListenersRef.current = eventListeners
      eventListeners.forEach(({ element, event, handler, options }) => {
        element.addEventListener(event, handler, options)
      })
    },
    [rotation, calculateRotation, throttledSetRotation]
  )

  const handleToolClose = useCallback(
    (e) => {
      e?.stopPropagation()
      e?.preventDefault()
      onToolClose()
    },
    [onToolClose]
  )

  // Memoize transform styles to prevent unnecessary re-renders
  const transformStyle = useMemo(
    () => ({
      transform: `rotate(${rotation}deg)`,
      transformOrigin: toolType === PROTRACTOR ? '48% 85%' : 'center center',
    }),
    [rotation, toolType]
  )

  // Memoize position for Rnd component
  const rndPosition = useMemo(
    () => ({
      x: position.x,
      y: position.y,
    }),
    [position.x, position.y]
  )

  // Memoize viewport dimensions to avoid recalculation
  const viewportDimensions = useMemo(
    () => ({
      width: window.innerWidth,
      height: window.innerHeight - 70, // bottom margin
    }),
    []
  )

  // Memoize drag stop handler with optimized calculations
  const handleDragStop = useCallback(
    (e, d) => {
      if (!contentRef.current) return

      const rect = contentRef.current.getBoundingClientRect()
      const allowedX = rect.width * 0.4
      const allowedY = rect.height * 0.4

      // Calculate maximum allowed positions using memoized viewport dimensions
      const maxX = viewportDimensions.width - rect.width + allowedX
      const maxY = viewportDimensions.height - rect.height + allowedY

      setPosition({
        x: Math.max(-allowedX, Math.min(d.x, maxX)),
        y: Math.max(-allowedY, Math.min(d.y, maxY)),
      })
    },
    [viewportDimensions]
  )

  // Memoize image styles to prevent unnecessary re-renders
  const imageStyle = useMemo(
    () => ({
      display: 'block',
      pointerEvents: 'none',
    }),
    []
  )

  // Memoize Rnd component style to prevent unnecessary re-renders
  const rndStyle = useMemo(
    () => ({
      zIndex: 1050,
    }),
    []
  )

  // Cleanup animation frame and event listeners on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      // Clean up any remaining event listeners
      eventListenersRef.current.forEach(
        ({ element, event, handler, options }) => {
          element.removeEventListener(event, handler, options)
        }
      )
    }
  }, [])

  return (
    <Rnd
      position={rndPosition}
      onDragStop={handleDragStop}
      style={rndStyle}
      enableResizing={false}
    >
      <div ref={contentRef} style={transformStyle}>
        {toolType === RULER && (
          <>
            <RightRotateIconContainer
              onMouseDown={handleRotationStart}
              onTouchStart={handleRotationStart}
            >
              <IconRightRotate color="none" />
            </RightRotateIconContainer>
            <LeftRotateIconContainer
              onMouseDown={handleRotationStart}
              onTouchStart={handleRotationStart}
            >
              <IconLeftRotate color="none" />
            </LeftRotateIconContainer>
            <RulerCrossButtonContainer onClick={handleToolClose}>
              <IconCrossYtSearch fillColor={white} />
            </RulerCrossButtonContainer>
            <img src={RulerSvg} alt="Ruler tool" style={imageStyle} />
          </>
        )}
        {toolType === PROTRACTOR && (
          <>
            <ProtractorLeftRotateIconContainer
              onMouseDown={handleRotationStart}
              onTouchStart={handleRotationStart}
            >
              <TransformedLeftRotateIcon color="none" />
            </ProtractorLeftRotateIconContainer>
            <ProtractorCrossButtonContainer onClick={handleToolClose}>
              <IconCrossYtSearch fillColor={white} />
            </ProtractorCrossButtonContainer>
            <img src={ProtractorSvg} alt="Protractor tool" style={imageStyle} />
          </>
        )}
      </div>
    </Rnd>
  )
}

export default DraggableGeometryTool
